import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import { 
  MapPin, Briefcase, DollarSign, Clock, ArrowLeft, Users,
  CheckCircle, Building, Mail, Phone, User, Edit, Calendar,
  Globe, Award, Share2, Check, RotateCcw
} from 'lucide-react';

const PositionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPositionDetails();
    fetchApplications();
  }, [id]);

  const fetchPositionDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/positions/${id}`);
      if (response.data.success) {
        setPosition(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching position:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions/${id}/applications`
      );
      if (response.data.success) {
        setApplications(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  // Build public share URL using org slug
  const getShareUrl = () => {
    const orgSlug = position?.organizations?.slug || position?.organization_slug || '';
    if (orgSlug) {
      return `${window.location.origin}/${orgSlug}/jobs/${id}`;
    }
    return window.location.href;
  };

  const handleShare = async () => {
    const url = getShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-700',
      draft: 'bg-yellow-100 text-yellow-700',
      closed: 'bg-red-100 text-red-700',
      filled: 'bg-blue-100 text-blue-700'
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.draft}`} data-testid="position-status-badge">
        {status || 'draft'}
      </span>
    );
  };

  if (loading) {
    return (
      <RecruiterLayout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Loading position details...</p>
        </div>
      </RecruiterLayout>
    );
  }

  if (!position) {
    return (
      <RecruiterLayout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Position not found</p>
        </div>
      </RecruiterLayout>
    );
  }

  const canEdit = position.organization_id === userProfile?.organizationId;

  return (
    <RecruiterLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button + Edit */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/positions')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            data-testid="back-to-positions"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Positions
          </button>
          {canEdit && (
            <div className="flex gap-2">
              <button
                onClick={handleShare}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium inline-flex items-center gap-2"
                data-testid="share-position-btn"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Share'}
              </button>
              {(position?.status === 'closed' || (() => {
                const pub = new Date(position?.published_at || position?.created_at);
                const closes = position?.closes_at ? new Date(position.closes_at) : new Date(pub.getTime() + 90*24*60*60*1000);
                return new Date() > closes;
              })()) && (
                <button
                  onClick={async () => {
                    try {
                      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/positions/${id}`, {
                        status: 'published',
                        closes_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                      });
                      fetchPositionDetails();
                    } catch (err) { console.error(err); }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium inline-flex items-center gap-2"
                  data-testid="reopen-position-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reopen
                </button>
              )}
              <button
                onClick={() => navigate(`/positions/${id}/edit`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium inline-flex items-center gap-2"
                data-testid="edit-position-btn"
              >
                <Edit className="w-4 h-4" />
                Edit Position
              </button>
            </div>
          )}
        </div>

        {/* Position Header — same structure as JobDetail */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6" data-testid="position-header">
          <div className="flex items-start gap-6 mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-10 h-10 text-white" />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900" data-testid="position-title">
                  {position.title}
                </h1>
                {getStatusBadge(position.status)}
              </div>
              <p className="text-xl text-gray-700 mb-4">
                {position.company_name || position.organization_name}
              </p>
              
              {/* Quick Info */}
              <div className="flex flex-wrap gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{position.location || 'Location not specified'}</span>
                </div>
                
                {position.employment_type && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span className="capitalize">{position.employment_type}</span>
                  </div>
                )}

                {position.workplace && (
                  <div className="flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    <span className="capitalize">{position.workplace}</span>
                  </div>
                )}

                {(position.salary_min || position.salary_max) && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    <span>
                      {position.salary_currency || '€'} {position.salary_min?.toLocaleString()}
                      {position.salary_max && ` - ${position.salary_max.toLocaleString()}`}
                    </span>
                  </div>
                )}

                {position.experience_level && (
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    <span className="capitalize">{position.experience_level}</span>
                  </div>
                )}

                {position.number_of_positions > 1 && (
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    <span>{position.number_of_positions} openings</span>
                  </div>
                )}
              </div>
            </div>

            {/* Applications Count */}
            <div className="flex-shrink-0 text-center" data-testid="applications-count">
              <div className="bg-blue-50 rounded-lg px-6 py-4">
                <p className="text-2xl font-bold text-blue-900">{applications.length}</p>
                <p className="text-sm text-blue-700">Application(s)</p>
              </div>
            </div>
          </div>

          {/* Skills */}
          {position.must_have_skills && position.must_have_skills.length > 0 && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Required Skills</h3>
              <div className="flex flex-wrap gap-2">
                {position.must_have_skills.map((skill, idx) => (
                  <span key={idx} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {position.nice_to_have_skills && position.nice_to_have_skills.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Nice to Have</h3>
              <div className="flex flex-wrap gap-2">
                {position.nice_to_have_skills.map((skill, idx) => (
                  <span key={idx} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Languages */}
          {position.languages && position.languages.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Required Languages</h3>
              <div className="flex flex-wrap gap-2">
                {position.languages.map((lang, idx) => (
                  <span key={idx} className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                    <Globe className="w-3 h-3 inline mr-1" />{lang}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Job Description — same 4 sections as JobDetail */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6" data-testid="position-description">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Description</h2>
          
          {position.description && position.description !== '<p><br></p>' && (
            <div className="mb-6" data-testid="position-description-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">About the Role</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.description }} />
            </div>
          )}

          {position.responsibilities && position.responsibilities !== '<p><br></p>' && (
            <div className="mb-6" data-testid="position-responsibilities-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Responsibilities</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.responsibilities }} />
            </div>
          )}

          {position.profile_requirements && position.profile_requirements !== '<p><br></p>' && (
            <div className="mb-6" data-testid="position-profile-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Profile & Requirements</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.profile_requirements }} />
            </div>
          )}

          {position.benefits && position.benefits !== '<p><br></p>' && (
            <div className="mb-6" data-testid="position-benefits-section">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Benefits</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.benefits }} />
            </div>
          )}

          {position.requirements && position.requirements !== '<p><br></p>' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h3>
              <div className="text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: position.requirements }} />
            </div>
          )}
        </div>

        {/* Recruiter-only: Contact Information */}
        {(position.contact_name || position.company_email || position.contact_phone) && (
          <div className="bg-white rounded-lg shadow-md p-8 mb-6" data-testid="position-contact-info">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {position.contact_name && (
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Contact Person</p>
                    <p className="font-medium text-gray-900">{position.contact_name}</p>
                    {position.contact_title && (
                      <p className="text-sm text-gray-600">{position.contact_title}</p>
                    )}
                  </div>
                </div>
              )}

              {position.company_email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a href={`mailto:${position.company_email}`} className="font-medium text-blue-600 hover:underline">{position.company_email}</a>
                  </div>
                </div>
              )}

              {position.contact_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">{position.contact_phone}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recruiter-only: Timeline & Details */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6" data-testid="position-timeline">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Position Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {position.notice_period && (
              <div>
                <p className="text-sm text-gray-500">Notice Period</p>
                <p className="font-medium text-gray-900">{position.notice_period}</p>
              </div>
            )}
            {position.industry && (
              <div>
                <p className="text-sm text-gray-500">Industry</p>
                <p className="font-medium text-gray-900">{position.industry}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">Created</p>
              <p className="font-medium text-gray-900 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(position.created_at).toLocaleDateString()}
              </p>
            </div>
            {position.published_at && (
              <div>
                <p className="text-sm text-gray-500">Published</p>
                <p className="font-medium text-green-700 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  {new Date(position.published_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Recruiter-only: Applications Section */}
        {applications.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-8" data-testid="position-applications">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent Applications</h2>
            <div className="space-y-4">
              {applications.slice(0, 10).map((app) => (
                <div key={app.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {app.prospects?.users?.first_name} {app.prospects?.users?.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{app.prospects?.users?.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {new Date(app.applied_at).toLocaleDateString()}
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        app.status === 'reviewing' ? 'bg-blue-100 text-blue-700' :
                        app.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {app.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </RecruiterLayout>
  );
};

export default PositionDetail;
